import { axiosInstance } from '@/plugins/axios'
import type { NewsPost } from '@/components/appDriver/newsfeed/types'
import { filterVisibleNewsPosts, sortNewsPosts } from '@/components/appDriver/newsfeed/utils'

type NewsCategory = NewsPost['category']
type NewsPriority = NewsPost['priority']
type NewsStatus = NewsPost['status']

type RawNewsPost = Record<string, any>

const NEWSFEED_ENDPOINT = '/api/v1/driver-center/newsfeeds'

function normalizeCategory(value: unknown): NewsCategory {
  const category = `${value ?? ''}`.toLowerCase()

  if (category === 'safety' || category === 'promotion' || category === 'training' || category === 'system') {
    return category
  }

  return 'operations'
}

function normalizePriority(value: unknown): NewsPriority {
  const priority = `${value ?? ''}`.toLowerCase()

  if (priority === 'important' || priority === 'urgent') {
    return priority
  }

  return 'normal'
}

function normalizeStatus(value: unknown): NewsStatus {
  const status = `${value ?? ''}`.toLowerCase()

  if (status === 'draft' || status === 'archived') {
    return status
  }

  return 'published'
}

function getStringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }
  }

  return undefined
}

function extractListPayload(response: any): RawNewsPost[] {
  const candidates = [
    response?.data?.items,
    response?.data?.newsfeeds,
    response?.data,
    response?.items,
    response?.newsfeeds,
    response,
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
    }
  }

  return []
}

function extractDetailPayload(response: any): RawNewsPost | null {
  const candidates = [
    response?.data?.newsfeed,
    response?.data,
    response?.newsfeed,
    response,
  ]

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate
    }
  }

  return null
}

function mapNewsPost(rawPost: RawNewsPost): NewsPost {
  const publishAt = getStringValue(
    rawPost.publishAt,
    rawPost.publish_at,
    rawPost.createdAt,
    rawPost.created_at,
    rawPost.updatedAt,
    rawPost.updated_at,
  ) ?? new Date(0).toISOString()

  return {
    id: getStringValue(rawPost.id, rawPost._id, rawPost.uuid) ?? publishAt,
    title: getStringValue(rawPost.title, rawPost.name) ?? '',
    summary: getStringValue(rawPost.summary, rawPost.description),
    content: getStringValue(rawPost.content, rawPost.body, rawPost.description, rawPost.summary) ?? '',
    category: normalizeCategory(rawPost.category),
    priority: normalizePriority(rawPost.priority),
    isPinned: Boolean(rawPost.isPinned ?? rawPost.is_pinned),
    publishAt,
    lastUpdate: getStringValue(rawPost.lastUpdate, rawPost.last_update, rawPost.updatedAt, rawPost.updated_at, publishAt) ?? publishAt,
    expireAt: getStringValue(rawPost.expireAt, rawPost.expire_at),
    status: normalizeStatus(rawPost.status),
    coverImage: getStringValue(rawPost.coverImage, rawPost.cover_image, rawPost.image, rawPost.image_url, rawPost.posterImage, rawPost.poster_image),
    ctaLabel: getStringValue(rawPost.ctaLabel, rawPost.cta_label),
    ctaLink: getStringValue(rawPost.ctaLink, rawPost.cta_link),
  }
}

export async function getNewsPostsForDriver(): Promise<NewsPost[]> {
  const { data } = await axiosInstance.get(NEWSFEED_ENDPOINT, {
    baseURL: import.meta.env.VITE_API_LOCA_URL,
  })

  const posts = extractListPayload(data).map(mapNewsPost)

  return sortNewsPosts(filterVisibleNewsPosts(posts))
}

export async function getNewsPostDetail(postId: string): Promise<NewsPost | null> {
  const { data } = await axiosInstance.get(`${NEWSFEED_ENDPOINT}/${postId}`, {
    baseURL: import.meta.env.VITE_API_LOCA_URL,
  })

  const post = extractDetailPayload(data)

  if (!post) {
    return null
  }

  return mapNewsPost(post)
}
